import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

// A text input that suggests while typing (items or suppliers of SAP's catalog). The list is drawn over the page
// (position: fixed), so a table's own scrolling never cuts it. Arrows and Enter pick; Escape closes; typing a free
// value is always allowed -- a suggestion helps, it never gates.
//   buscar(q) -> Promise<array>     alElegir(item)      etiqueta(item) -> node
export default function Autocompletar({
  value, onChange, buscar, alElegir, etiqueta, minimo = 2, className = '', onKeyDown, onBlur, ...resto
}) {
  const [items, setItems] = useState([])
  const [abierto, setAbierto] = useState(false)
  const [activo, setActivo] = useState(-1)
  const [caja, setCaja] = useState(null)
  const ref = useRef(null)
  const enfocado = useRef(false)
  const pedido = useRef(0)

  const medir = useCallback(() => {
    const r = ref.current?.getBoundingClientRect()
    if (r) setCaja({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 340) })
  }, [])

  useEffect(() => {
    const q = (value || '').trim()
    if (!enfocado.current || q.length < minimo) return undefined
    const mio = ++pedido.current
    const h = setTimeout(() => {
      buscar(q).then(r => {
        if (mio !== pedido.current || !enfocado.current) return
        setItems(r || [])
        setActivo(-1)
        setAbierto((r || []).length > 0)
        medir()
      }).catch(() => {})                 // suggestions are a help: if they cannot be asked, the field still works
    }, 250)
    return () => clearTimeout(h)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (!abierto) return undefined
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    return () => { window.removeEventListener('scroll', medir, true); window.removeEventListener('resize', medir) }
  }, [abierto, medir])

  const elegir = (item) => {
    setAbierto(false)
    pedido.current += 1
    alElegir(item)
  }

  const teclas = (e) => {
    if (abierto && items.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActivo(a => Math.min(items.length - 1, a + 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(a => Math.max(0, a - 1)); return }
      if (e.key === 'Enter' && activo >= 0) { e.preventDefault(); e.stopPropagation(); elegir(items[activo]); return }
      if (e.key === 'Escape') { e.stopPropagation(); setAbierto(false); return }
    }
    onKeyDown?.(e)
  }

  return (
    <>
      <input ref={ref} value={value} className={className} autoComplete="off"
        onChange={e => { enfocado.current = true; onChange(e.target.value) }}
        onFocus={() => { enfocado.current = true }}
        onBlur={e => { enfocado.current = false; setTimeout(() => setAbierto(false), 150); onBlur?.(e) }}
        onKeyDown={teclas} {...resto} />
      {abierto && caja && createPortal(
        <ul role="listbox" style={{ position: 'fixed', left: caja.left, top: caja.top, width: caja.width, zIndex: 60 }}
          className="max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm">
          {items.map((it, i) => (
            <li key={i} role="option" aria-selected={i === activo}
              onMouseDown={e => { e.preventDefault(); elegir(it) }}
              onMouseEnter={() => setActivo(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === activo ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
              {etiqueta(it)}
            </li>
          ))}
        </ul>, document.body)}
    </>
  )
}
